import React from 'lib/react';
import PropTypes from 'prop-types';
import {
  EVDeleteIcon, EVGrid, EVBusinessOutlinedIcon, EVHomeOutlinedIcon, EVMultiFamilyIcon,
  EVButton, EVCircularProgress,
  // EVTypography,
} from 'lib/components';
import { COMMERCIAL, RESIDENTIAL, PIN_COLORS } from 'constants/AppConstants';
import clsx from 'lib/clsx';
import { LOADING_ADDRESS, ERROR_LOADING_ADDRESS } from 'features/placeOrder/OrderAddresses.constants';
import { useTranslation } from 'lib/translation';
import MapPinStyles from './MapPinStyles';
/** Component to render the Pins to point the located address on map */
function MapPin(props) {
  const {
    pinIndex,
    isComplex,
    propertyType,
    buildingID,
    address: {
      address,
      address2,
      city,
      state,
      zip,
    },
    colors,
    focusedPinIndex,
    pinDragEnabled,
  } = props;
  const hasDeleteButton = isComplex && pinDragEnabled;
  const classes = MapPinStyles({
    hasDeleteButton, pinIndex, colors, focusedPinIndex,
  });

  const { t } = useTranslation();

  const building = buildingID || pinIndex + 1;
  // #genai: extract inner template literal to a variable to avoid nested template literals (Sonar S4624); value/behavior unchanged
  const cityStateZip = `${city}, ${state} ${zip}`;
  const pinAriaLabel = `Dropped pin, building ${building}, ${propertyType} property, ${address} ${address2 || cityStateZip}`;
  const deleteButtonAriaLabel = `Delete building ${building}`;

  const isAddressLoading = address === LOADING_ADDRESS;
  const isErrorAddress = address === ERROR_LOADING_ADDRESS;

  const addressDiv = () => {
    if (isAddressLoading) return <strong>{t('order__loading_address')}</strong>;

    if (isErrorAddress) {
      return (
        <EVButton id={`tryAgain-${pinIndex}`}>
          <strong>{t('order__try_again')}</strong>
        </EVButton>
      );
    }

    return (
      <>
        <strong>{address}</strong>
        <br />
        {address2 || cityStateZip}
      </>
    );
  };

  const buttonDiv = () => {
    if (isAddressLoading) return <EVCircularProgress size={24} style={{ color: '#007CAD' }} />;

    if (isErrorAddress) return <div style={{ width: '24px' }} />;

    return hasDeleteButton && (
      <EVButton id={`addressDeleteBtn-${pinIndex}`} className={classes.addressDeleteBtn} aria-label={deleteButtonAriaLabel}>
        <EVDeleteIcon htmlColor="#82899C" style={{ paddingLeft: '5px' }} />
      </EVButton>
    );
  };

  // #genai: extract nested ternary into an independent helper function (Sonar S3358); evaluation order/behavior unchanged
  const propertyTypeIcon = () => {
    if (propertyType === COMMERCIAL) return <EVBusinessOutlinedIcon tabindex="-1" aria-hidden="true" />;
    if (propertyType === RESIDENTIAL) return <EVHomeOutlinedIcon tabindex="-1" aria-hidden="true" />;
    return <EVMultiFamilyIcon tabindex="-1" aria-hidden="true" />;
  };

  return (
    <>
      <EVGrid id={`mapPin-${pinIndex}`} className={classes.mapPin} aria-label={pinAriaLabel}>
        <EVGrid id="pinContent" className={classes.pinContent}>
          <EVGrid className={classes.numberDiv}>
            {propertyTypeIcon()}
            {
              buildingID
                ? (
                  <span>
                    {buildingID.slice(0, 10)}
                    {buildingID.slice(10) ? '...' : ''}
                  </span>
                )
                : isComplex && <span>{pinIndex + 1}</span>
            }
          </EVGrid>
          <EVGrid id={`addressDiv-${pinIndex}`} className={clsx(classes.addressDiv, classes.addressText, focusedPinIndex === pinIndex && 'focused')}>
            {addressDiv()}
          </EVGrid>
          {buttonDiv()}
        </EVGrid>
        <EVGrid className={classes.arrow} />
      </EVGrid>
      <EVGrid className={classes.point} />
    </>
  );
}

MapPin.defaultProps = {
  isComplex: false,
  focusedPinIndex: 0,
  buildingID: '',
  pinDragEnabled: false,
  colors: PIN_COLORS,
};

MapPin.propTypes = {
  pinIndex: PropTypes.number.isRequired,
  address: PropTypes.shape().isRequired,
  isComplex: PropTypes.bool,
  buildingID: PropTypes.string,
  propertyType: PropTypes.oneOf([RESIDENTIAL, COMMERCIAL]).isRequired,
  focusedPinIndex: PropTypes.number,
  pinDragEnabled: PropTypes.bool,
  colors: PropTypes.arrayOf(PropTypes.string),
};

export default MapPin;
